import React, {Fragment, useEffect, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {
  Checkbox,
  createStyles,
  Dialog,
  DialogContent,
  Divider,
  FormControl,
  FormControlLabel,
  IconButton,
  makeStyles,
  Radio,
  RadioGroup,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Theme,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import {
  INDIVIDUAL_REANALYSIS_MANUAL_TYPE,
  PACKAGE_TYPE,
  TypeIndividualReanalysisManualType,
} from 'constants/AnalysisConstant'
import {StyledFieldSet} from 'pages/Analysis/EEGIndividual/ReAnalysis/StyledFieldSet'
import {StyledIcaDialog} from 'pages/Analysis/EEGIndividual/ReAnalysis/StyledIcaDialog'
import useProductManagement from 'features/productManagement/useProductManagement'
import {
  EEG_INDICATOR_ID,
  MCI_CLINIC_INDICATOR_ID,
} from 'pages/ProductsPages/constant'
import {
  checkAvailability3DAnalysisApi,
  checkAvailabilityBrainConnectivityApi,
  checkAvailabilityFiveIndicatorApi,
  checkAvailabilityMciApi,
  checkAvailabilityNorm3DApi,
  checkAvailabilityNormApi,
  checkAvailabilityReanalysisApi,
  checkAvailabilitySummaryAnalysisApi,
  reAnalysisHealthCareApi,
  reAnalysisIndividual3DApi,
  reAnalysisIndividualApi,
  reAnalysisIndividualBrainConnectivityApi,
  reAnalysisIndividualFiveIndicatorApi,
  reAnalysisIndividualHrvApi,
  reAnalysisIndividualMciApi,
  reAnalysisIndividualNormApi,
  reAnalysisIndividualSummaryApi,
  SearchedIndividualEEG,
  SummaryAvailableAnalysis,
  ZScore,
} from 'api/analysisApi'
import {isoStringToDateAndTimeString} from 'helpers/dateHelper'
import {getEyeStatus} from 'helpers/analysisHelper'
import {AppLoading} from 'AppLoading'
import ConfirmButton from 'components/atoms/Buttons/ConfirmButton'
import CancelButton from 'components/atoms/Buttons/CancelButton'
import useFailureModal from 'features/modal/useFailureModal'

interface IReanalysisDialogProps {
  open: boolean
  onClose: () => void
  selectedIndividual: SearchedIndividualEEG
}

const styles = (theme: Theme) =>
  createStyles({
    container: {
      '& > .MuiDialog-container > .MuiPaper-root': {
        [theme.breakpoints.up('xs')]: {
          minWidth: 354,
        },
        [theme.breakpoints.up('sm')]: {
          minWidth: 589,
        },
        [theme.breakpoints.up('md')]: {
          minWidth: 700,
          height: 840,
        },
      },
      position: 'relative',
    },
    contentContainer: {
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      overflowY: 'scroll',
      height: 700,
      border: '1px solid #dcdcdc',
      background: 'white',
      padding: 20,
      marginTop: 10,
    },
    title: {
      fontSize: 28,
      fontWeight: 700,
      textAlign: 'center',
      color: '#333',
    },
    checkboxLabel: {
      width: 'fit-content',
    },
    radioWrap: {
      display: 'flex',
      flexDirection: 'row',
      alignContent: 'center',
      alignItems: 'center',
      justifyContent: 'space-between',
      '& label > span': {
        marginRight: 70,
        color: '#555555',
        fontSize: 13,
      },
      '& label > span > span > div > .MuiSvgIcon-fontSizeSmall': {
        fontSize: 17,
      },
    },
    radioText: {
      color: '#555555',
      fontSize: 13,
    },
    line: {
      color: '#fafafa',
      backgroundColor: '#fafafa',
      margin: '5px 0',
    },
    closeButton: {
      backgroundColor: '#f4f4f4',
      width: 20,
      height: 20,
    },
    closeButtonWrap: {
      display: 'flex',
      justifyContent: 'flex-end',
      marginRight: '15px',
      marginTop: '10px',
    },
    closeIcon: {
      width: 20,
      height: 20,
      color: '#c5c5c5',
    },
  })
const useStyles = makeStyles(styles)

const healthCareTypes = [
  PACKAGE_TYPE.AI_BASIC,
  PACKAGE_TYPE.AI_STANDARD,
  PACKAGE_TYPE.AI_PREMIUM,
  PACKAGE_TYPE.AI_PREMIUM_PLUS,
  PACKAGE_TYPE.AI_HRV_ONLY,
  PACKAGE_TYPE.AI_BASIC_HRV,
  PACKAGE_TYPE.AI_STANDARD_HRV,
  PACKAGE_TYPE.AI_PREMIUM_HRV,
  PACKAGE_TYPE.AI_PREMIUM_PLUS_HRV,
]

const ReAnalysisRadioValue = [
  {label: 'IReAnalysis_auto', value: 'auto'},
  {label: 'IReAnalysis_manual', value: 'manual'},
]

const ManualReAnalysisRadioValue = [
  {
    label: 'IReAnalysis_manual_re-select',
    value: INDIVIDUAL_REANALYSIS_MANUAL_TYPE.SELECT,
  },
  {
    label: 'IReAnalysis_manual_maintain',
    value: INDIVIDUAL_REANALYSIS_MANUAL_TYPE.PREVIOUS,
  },
]

// 뇌 기능 매핑(기본/고급)의 PAIRED DATA 선택 영역 표시 여부.
// 현재는 UI에서 숨기고 SUCCESS 중 가장 최근 분석을 자동 선택한다(checkSummaryPairedData).
// 다시 노출하려면 true로 변경한다.
const IS_PAIRED_DATA_SECTION_VISIBLE = false

const Z_SCORE_OPTIONS: ZScore[] = [1.96, 2.58, 3]

const EmptyContentPackageTypes = [
  PACKAGE_TYPE.INDIVIDUAL_3D,
  PACKAGE_TYPE.FIVE_INDICATOR,
  PACKAGE_TYPE.BRAIN_FUNCTION_CONNECTIVITY,
  PACKAGE_TYPE.NORM_3D,
  PACKAGE_TYPE.MCI5,
  PACKAGE_TYPE.MCI8,
  PACKAGE_TYPE.HEALTH_CARE,
  PACKAGE_TYPE.AI_HRV_ONLY,
  PACKAGE_TYPE.AI_BASIC_HRV,
  PACKAGE_TYPE.AI_STANDARD_HRV,
  PACKAGE_TYPE.AI_PREMIUM_HRV,
  PACKAGE_TYPE.AI_PREMIUM_PLUS_HRV,
]

type ZScoreAvailableMap = Record<ZScore, boolean>

const DefaultZScoreAvailableMap: ZScoreAvailableMap = {
  1.96: true,
  2.58: true,
  3: true,
}

const ReanalysisDialog = (props: IReanalysisDialogProps) => {
  const {open, onClose, selectedIndividual} = props
  const {t, i18n} = useTranslation()
  const classes = useStyles()
  const {listAvailableLicenses, listPackage} = useProductManagement()
  const [listCheckedPackage, setListCheckedPackage] = useState<string[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isShowPairedData, setIsShowPairedData] = useState(false)
  const [isResultView, setIsResultView] = useState(false)
  const {onOpen: onFailureModalOpen} = useFailureModal()
  const [resultSection, setResultSection] = useState<
    Record<string, React.ReactNode | null>
  >({})

  // ICA re-analysis state
  const [reAnalysis, setReAnalysis] = useState<'auto' | 'manual'>('auto')
  const [isAgreeAutoDenoise, setIsAgreeAutoDenoise] = useState(false)
  const [manualReAnalysis, setManualReAnalysis] =
    useState<TypeIndividualReanalysisManualType>(
      INDIVIDUAL_REANALYSIS_MANUAL_TYPE.PREVIOUS,
    )
  const [isSraYn, setIsSraYn] = useState(false)

  // Summary state

  const [summaryInformation, setSummaryInformation] = useState<{
    eegIds: {
      analyzeAt: string
      analyzeId: number
      eceo: string
      status: string
    }[]
    young: boolean
    reportType: ReportType
  }>({eegIds: [], young: false, reportType: 'P1'})
  const [selectedPairEeg, setSelectedPairEeg] = useState<number>(0)

  // Norm state

  const [zScoreAvailableMap, setZScoreAvailableMap] =
    useState<ZScoreAvailableMap>(DefaultZScoreAvailableMap)

  const [zScore, setZScore] = useState('1.96')

  // Norm functions

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setZScore((event.target as HTMLInputElement).value)
  }

  // Summary functions

  const tableHeader = [
    {
      id: 'select',
      label: t('ISelectCustomer'),
    },
    {
      id: 'number',
      label: t('IIndividualAnalysisNumber'),
    },
    {
      id: 'eyes',
      label: t('IECEO'),
    },
    {
      id: 'date',
      label: t('IMeasureDate'),
    },
  ]

  // ICA functions

  const handleReAnalysisTypeChanged = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setReAnalysis(event.target.value as 'auto' | 'manual')
  }

  const handleManualReAnalysisChanged = (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => {
    setIsSraYn(false)
    setManualReAnalysis((event.target as HTMLInputElement).value)
  }

  const renderPairedDataSection = () => (
    <Fragment>
      <div style={{color: '#333333'}}>
        <div>PAIRED DATA</div>
        <div
          style={{
            fontSize: 13,
          }}
        >
          {t('IPairDataDesc')}
        </div>
      </div>
      <Divider />
      <div style={{margin: 10}}>
        <Table size='small'>
          <TableHead>
            <TableRow>
              {tableHeader.map((headCell) => (
                <TableCell key={headCell.id}>{headCell.label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {summaryInformation.eegIds.map((pairInfo) => (
              <TableRow key={pairInfo.analyzeId}>
                <TableCell>
                  <Radio
                    checked={pairInfo.analyzeId === selectedPairEeg}
                    onChange={() => setSelectedPairEeg(pairInfo.analyzeId)}
                  />
                </TableCell>
                <TableCell>{pairInfo.analyzeId}</TableCell>
                <TableCell>{getEyeStatus(pairInfo.eceo)}</TableCell>
                <TableCell>
                  {isoStringToDateAndTimeString(pairInfo.analyzeAt)}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </Fragment>
  )

  const renderNormSection = () => (
    <div style={{margin: 10}}>
      <FormControl component='fieldset'>
        <RadioGroup
          aria-label='gender'
          name='zScore'
          value={zScore}
          onChange={handleChange}
        >
          {Z_SCORE_OPTIONS.map((value, index) => {
            const isAvailable = zScoreAvailableMap[value]

            return (
              <Fragment key={value}>
                <div className={classes.radioWrap}>
                  <FormControlLabel
                    value={String(value)}
                    control={<Radio color='primary' size='small' />}
                    label={String(value)}
                    disabled={!isAvailable}
                  />
                  <span className={classes.radioText}>
                    {t(
                      isAvailable
                        ? 'IAnalysisAvailable'
                        : 'IHasCompletionHistory',
                    )}
                  </span>
                </div>
                {index < Z_SCORE_OPTIONS.length - 1 && (
                  <Divider className={classes.line} />
                )}
              </Fragment>
            )
          })}
        </RadioGroup>
      </FormControl>
    </div>
  )

  const emptyContentPackages = Object.fromEntries(
    EmptyContentPackageTypes.map((type) => [type, null]),
  ) as Record<string, null>

  const contentPackage: Record<string, React.ReactNode | null> = {
    [PACKAGE_TYPE.INDIVIDUAL]: (
      <StyledIcaDialog>
        <div className='content'>
          <StyledFieldSet aria-label='ICA 종류' direction='row'>
            {ReAnalysisRadioValue.map((item) => (
              <label htmlFor={item.value} key={item.value}>
                <input
                  type='radio'
                  id={item.value}
                  name='ica'
                  value={item.value}
                  style={{marginTop: 3}}
                  checked={item.value === reAnalysis}
                  onChange={handleReAnalysisTypeChanged}
                />
                <span className='label'>{t(item.label)}</span>
              </label>
            ))}
          </StyledFieldSet>
          <div className='input-area' style={{padding: '20px 10px'}}>
            {reAnalysis === 'auto' && (
              <StyledFieldSet direction='column' style={{padding: '0 0 10px'}}>
                <label
                  htmlFor='agreeAutoDenoise'
                  style={{alignItems: 'flex-start'}}
                >
                  <input
                    type='checkbox'
                    id='agreeAutoDenoise'
                    name='agree_auto_denoise'
                    style={{marginTop: 4}}
                    checked={isAgreeAutoDenoise}
                    onChange={() => setIsAgreeAutoDenoise((b) => !b)}
                  />
                  <span className='label'>{t('IReAnalysis_agree')}</span>
                </label>
              </StyledFieldSet>
            )}
            {reAnalysis === 'manual' && (
              <StyledFieldSet direction='column' style={{padding: '0 0 10px'}}>
                {ManualReAnalysisRadioValue.map((item) => (
                  <label htmlFor={item.value} key={item.value}>
                    <input
                      type='radio'
                      id={item.value}
                      name='manual_ica'
                      value={item.value}
                      checked={item.value === manualReAnalysis}
                      onChange={handleManualReAnalysisChanged}
                    />
                    <span className='label'>{t(item.label)}</span>
                  </label>
                ))}
                <label htmlFor='signalRestoration'>
                  <input
                    type='checkbox'
                    id='signalRestoration'
                    name='agree_auto_denoise'
                    checked={isSraYn}
                    onChange={() => setIsSraYn((b) => !b)}
                    disabled={manualReAnalysis === 'maintain'}
                  />
                  <span
                    className='label'
                    style={{
                      opacity: manualReAnalysis === 'maintain' ? 0.7 : 1,
                    }}
                  >
                    {t('ISignalRestoration')}
                  </span>
                </label>
              </StyledFieldSet>
            )}
            <p
              style={{
                textAlign: 'left',
              }}
            >
              {t('IReAnalysis_notice')}
            </p>
          </div>
        </div>
      </StyledIcaDialog>
    ),
    [PACKAGE_TYPE.SUMMARY]:
      isShowPairedData && IS_PAIRED_DATA_SECTION_VISIBLE
        ? renderPairedDataSection()
        : null,
    [PACKAGE_TYPE.SUMMARY_ADVANCED]:
      isShowPairedData && IS_PAIRED_DATA_SECTION_VISIBLE
        ? renderPairedDataSection()
        : null,
    [PACKAGE_TYPE.NORM]: renderNormSection(),
    ...emptyContentPackages,
  }

  const listPackageItem = useMemo(() => {
    const uniquePackageItemIds = [
      ...new Set(listAvailableLicenses.map((l) => l.packageItemId)),
    ]
    return uniquePackageItemIds
      .map((packageItemId) => {
        const currentLicense = listPackage?.find(
          (item) => item.itemId === packageItemId,
        )
        if (!currentLicense) {
          return {
            packageItemId: String(packageItemId),
            cls: '',
            label: '',
            type: '',
            order: 0,
          }
        }
        const displayInfo = currentLicense.displayInfos?.find(
          (info) => info.langCode === (i18n.language === 'ko' ? 'ko' : 'en'),
        )
        return {
          packageItemId: String(currentLicense.itemId),
          cls: currentLicense.cls ?? '',
          label: displayInfo?.name ?? '',
          type: currentLicense.type,
          order: currentLicense.order,
        }
      })
      .filter(
        (item) =>
          item.cls.includes('EEG') &&
          item.packageItemId !== MCI_CLINIC_INDICATOR_ID &&
          item.packageItemId !== EEG_INDICATOR_ID,
      )
      .reduce(
        (
          acc: {
            packageItemId: string
            cls: string
            label: string
            type: string
            order: number
          }[],
          item,
        ) => {
          if (healthCareTypes.includes(item.type)) {
            if (!acc.some((i) => i.type === PACKAGE_TYPE.HEALTH_CARE)) {
              acc.push({
                ...item,
                label: t('IHealthCheckupAnalysis'),
                type: PACKAGE_TYPE.HEALTH_CARE,
                order: 1000,
              })
            }
          } else {
            acc.push(item)
          }
          return acc
        },
        [],
      )
      .toSorted((a, b) => a.order - b.order)
  }, [listAvailableLicenses, listPackage, i18n.language])

  const onCheckPackage = (packageType: string) => {
    setListCheckedPackage((prev) => {
      if (prev.includes(packageType)) {
        return prev.filter((type) => type !== packageType)
      }
      if (packageType === PACKAGE_TYPE.SUMMARY) {
        return [
          ...prev.filter((type) => type !== PACKAGE_TYPE.SUMMARY_ADVANCED),
          packageType,
        ]
      }
      if (packageType === PACKAGE_TYPE.SUMMARY_ADVANCED) {
        return [
          ...prev.filter((type) => type !== PACKAGE_TYPE.SUMMARY),
          packageType,
        ]
      }
      if ([PACKAGE_TYPE.MCI5, PACKAGE_TYPE.MCI8].includes(packageType)) {
        return [
          ...prev.filter(
            (type) => ![PACKAGE_TYPE.MCI5, PACKAGE_TYPE.MCI8].includes(type),
          ),
          packageType,
        ]
      }
      return [...prev, packageType]
    })
  }

  const checkSummaryPairedData = async (reportType: ReportType) => {
    const res = await checkAvailabilitySummaryAnalysisApi({
      eegIds: selectedIndividual.id,
    })
    if (res.data.available) {
      if (res.data.pairEegIds && res.data.pairEegIds.length > 0) {
        setIsShowPairedData(true)
        setSummaryInformation({
          eegIds: res.data.pairInfos,
          young: res.data.young,
          reportType,
        })
        const latestSuccessPair = res.data.pairInfos
          .filter((pairInfo) => pairInfo.status === 'SUCCESS')
          .reduce<SummaryAvailableAnalysis['pairInfos'][number] | undefined>(
            (latest, pairInfo) =>
              !latest ||
              new Date(pairInfo.analyzeAt) > new Date(latest.analyzeAt)
                ? pairInfo
                : latest,
            undefined,
          )
        setSelectedPairEeg(latestSuccessPair?.analyzeId ?? 0)
      } else setIsShowPairedData(false)
    }
    return res
  }

  const checkZScoreAvailability = async () => {
    const res = await checkAvailabilityNormApi(selectedIndividual.id)
    if (res.data.available) {
      setZScoreAvailableMap((prev) => {
        const updated: ZScoreAvailableMap = {
          1.96: prev[1.96],
          2.58: prev[2.58],
          3: prev[3],
        }
        res.data.zscores.forEach((z: ZScore) => {
          updated[z] = false
        })
        return updated
      })
      const newZScore =
        [1.96, 2.58, 3].find((z) => !res.data.zscores.includes(z as ZScore)) ||
        '1.96'
      setZScore(String(newZScore))
    }
    return res
  }

  const checkAvailablePackageApi = async (packageType: string) => {
    switch (packageType) {
      case PACKAGE_TYPE.INDIVIDUAL:
        return checkAvailabilityReanalysisApi(selectedIndividual.id)
      case PACKAGE_TYPE.INDIVIDUAL_3D:
        return checkAvailability3DAnalysisApi(selectedIndividual.id)
      case PACKAGE_TYPE.SUMMARY:
        return checkSummaryPairedData('P1')
      case PACKAGE_TYPE.SUMMARY_ADVANCED:
        return checkSummaryPairedData('P2')
      case PACKAGE_TYPE.NORM:
        return checkZScoreAvailability()
      case PACKAGE_TYPE.NORM_3D:
        return checkAvailabilityNorm3DApi(selectedIndividual.id)
      case PACKAGE_TYPE.MCI5:
        return checkAvailabilityMciApi({
          eegId: selectedIndividual.id,
          stage: '5',
        })
      case PACKAGE_TYPE.MCI8:
        return checkAvailabilityMciApi({
          eegId: selectedIndividual.id,
          stage: '8',
        })
      case PACKAGE_TYPE.FIVE_INDICATOR:
        return checkAvailabilityFiveIndicatorApi(selectedIndividual.id)
      case PACKAGE_TYPE.BRAIN_FUNCTION_CONNECTIVITY:
        return checkAvailabilityBrainConnectivityApi(selectedIndividual.id)
      default:
        return {data: {available: true}}
    }
  }

  const getPackageAvailableStatus = async () => {
    try {
      setIsLoading(true)
      await Promise.allSettled(
        listPackageItem?.map((item) => checkAvailablePackageApi(item.type)) ??
          [],
      )
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }

  const reAnalysisPackageApi = async (packageType: string) => {
    try {
      let result
      switch (packageType) {
        case PACKAGE_TYPE.INDIVIDUAL: {
          const payload = {
            agreeYn: (reAnalysis === 'auto' ? 'Y' : 'N') as YN,
            manualType:
              reAnalysis === 'auto'
                ? INDIVIDUAL_REANALYSIS_MANUAL_TYPE.NONE
                : manualReAnalysis,
            sraYn: (reAnalysis === 'manual' && isSraYn ? 'Y' : 'N') as YN,
          }
          result = await reAnalysisIndividualApi({
            individualId: selectedIndividual.id,
            data: payload,
          })
          break
        }
        case PACKAGE_TYPE.INDIVIDUAL_3D:
          result = await reAnalysisIndividual3DApi(selectedIndividual.id)
          break
        case PACKAGE_TYPE.SUMMARY:
          result = await reAnalysisIndividualSummaryApi({
            eegIds: [
              selectedIndividual.id,
              ...(selectedPairEeg !== 0 ? [selectedPairEeg] : []),
            ],
            reportType: 'P1',
          })
          break
        case PACKAGE_TYPE.SUMMARY_ADVANCED:
          result = await reAnalysisIndividualSummaryApi({
            eegIds: [
              selectedIndividual.id,
              ...(selectedPairEeg !== 0 ? [selectedPairEeg] : []),
            ],
            reportType: 'P2',
          })
          break
        case PACKAGE_TYPE.NORM:
          result = await reAnalysisIndividualNormApi({
            eegId: selectedIndividual.id,
            zscore: Number.parseFloat(zScore) as ZScore,
          })
          break
        case PACKAGE_TYPE.NORM_3D:
          result = await checkAvailabilityNorm3DApi(selectedIndividual.id)
          break
        case PACKAGE_TYPE.MCI5:
          result = await reAnalysisIndividualMciApi({
            eegId: selectedIndividual.id,
            stage: '5',
          })
          break
        case PACKAGE_TYPE.MCI8:
          result = await reAnalysisIndividualMciApi({
            eegId: selectedIndividual.id,
            stage: '8',
          })
          break
        case PACKAGE_TYPE.FIVE_INDICATOR:
          result = await reAnalysisIndividualFiveIndicatorApi(
            selectedIndividual.id,
          )
          break
        case PACKAGE_TYPE.BRAIN_FUNCTION_CONNECTIVITY:
          result = await reAnalysisIndividualBrainConnectivityApi(
            selectedIndividual.id,
          )
          break
        case PACKAGE_TYPE.HEALTH_CARE:
          result = await reAnalysisHealthCareApi(selectedIndividual.id)
          break
        case PACKAGE_TYPE.AI_HRV_ONLY:
        case PACKAGE_TYPE.AI_BASIC_HRV:
        case PACKAGE_TYPE.AI_STANDARD_HRV:
        case PACKAGE_TYPE.AI_PREMIUM_HRV:
        case PACKAGE_TYPE.AI_PREMIUM_PLUS_HRV:
          result = await reAnalysisIndividualHrvApi(
            Number(selectedIndividual.measureId),
          )
          break
        default:
          result = {
            data: {
              available: true,
            },
          }
      }
      return {type: packageType, result}
    } catch (error) {
      return {type: packageType, error}
    }
  }

  const confirmReanalysis = async () => {
    try {
      setIsLoading(true)
      if (
        listCheckedPackage.includes(PACKAGE_TYPE.INDIVIDUAL) &&
        reAnalysis === 'auto' &&
        !isAgreeAutoDenoise
      ) {
        onFailureModalOpen(t('IRegisterTermsNotAgree'))
        return
      }
      const res = await Promise.allSettled(
        listCheckedPackage.map((type) => reAnalysisPackageApi(type)),
      )
      setResultSection(
        res.reduce((acc, cur) => {
          if (cur.status === 'fulfilled') {
            acc[cur.value.type] = (
              <li
                style={{
                  listStyleType: 'disc',
                }}
              >
                <div
                  style={{
                    marginLeft: 10,
                  }}
                >
                  {t('IStatusResult')}:{' '}
                  {cur.value.error ? cur.value.error.message : t('ISuccess')}
                </div>
              </li>
            )
          }
          return acc
        }, {} as Record<string, React.ReactNode | null>),
      )
      setIsResultView(true)
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }

  const handleClose = () => {
    setZScoreAvailableMap(DefaultZScoreAvailableMap)
    setSummaryInformation({
      eegIds: [],
      young: false,
      reportType: 'P1',
    })
    setResultSection({})
    onClose()
  }

  useEffect(() => {
    getPackageAvailableStatus()
  }, [listPackageItem])

  return (
    <Dialog open={open} onClose={onClose} className={classes.container}>
      {isLoading ? (
        <AppLoading loading />
      ) : (
        <Fragment>
          <div className={classes.closeButtonWrap}>
            <IconButton
              color='secondary'
              aria-label='favorite'
              className={classes.closeButton}
              onClick={onClose}
            >
              <CloseIcon className={classes.closeIcon} />
            </IconButton>
          </div>
          <DialogContent>
            <div className={classes.contentContainer}>
              <div className={classes.title}>
                {isResultView
                  ? t('IAnalysisRequestResults')
                  : t('ISelectAnalysisIndicators')}
              </div>
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 20,
                  marginTop: 20,
                }}
              >
                {(isResultView
                  ? listPackageItem.filter((item) =>
                      listCheckedPackage.includes(item.type),
                    )
                  : listPackageItem
                ).map((item) => (
                  <div
                    key={item.packageItemId}
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 10,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 10,
                      }}
                    >
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={listCheckedPackage.includes(item.type)}
                            disabled={isResultView}
                            onChange={() => onCheckPackage(item.type)}
                            name={item.label}
                            color='primary'
                          />
                        }
                        label={
                          <div
                            style={{
                              fontSize: 20,
                              fontWeight: 700,
                              color: 'black',
                            }}
                          >
                            {item.label}
                          </div>
                        }
                        className={classes.checkboxLabel}
                      />
                    </div>
                    {isResultView ? (
                      <ul
                        style={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 10,
                          marginLeft: 20,
                        }}
                      >
                        {resultSection[item.type]}
                      </ul>
                    ) : (
                      listCheckedPackage.includes(item.type) &&
                      contentPackage[item.type]
                    )}
                  </div>
                ))}
              </div>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginTop: 20,
                gap: 40,
              }}
            >
              {!isResultView && <CancelButton onClick={handleClose} />}
              <ConfirmButton
                onClick={isResultView ? handleClose : confirmReanalysis}
                disabled={!listCheckedPackage.length}
              />
            </div>
          </DialogContent>
        </Fragment>
      )}
    </Dialog>
  )
}

export default ReanalysisDialog
